import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Button, ButtonToolbar } from 'reactstrap';
import { useDispatch, flashError, flashSuccess } from '@/shared/components/flash';
import { getEventTypeAccess } from '@/services/getEventTypeAccess';
import { saveEventTypeAccess } from '@/services/saveEventTypeAccess';
import { ToggleButtonField } from '@/shared/components/form/ToggleButton';

const EventTypeAccessModal = ({ isOpen, toggle, program, organization }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [accessMap, setAccessMap] = useState({});

  const load = useCallback(async () => {
    if (!organization?.id || !program?.id) return;
    setLoading(true);
    try {
      const settings = await getEventTypeAccess(organization.id, program.id);
      const typeList = Array.isArray(settings) ? settings : [];
      setEventTypes(typeList);
      const map = {};
      typeList.forEach((et) => {
        map[et.event_type_id] = Boolean(et.manager_access);
      });
      setAccessMap(map);
    } catch {
      flashError(dispatch, 'Unable to load event type settings.');
    } finally {
      setLoading(false);
    }
  }, [organization?.id, program?.id, dispatch]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const onToggle = (eventTypeId, value) => {
    setAccessMap((prev) => ({ ...prev, [eventTypeId]: value }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(accessMap).map(([id, access]) => ({
        event_type_id: parseInt(id, 10),
        manager_access: access ? 1 : 0,
      }));
      await saveEventTypeAccess(organization.id, program.id, payload);
      flashSuccess(dispatch, 'Event type access saved.');
      toggle(true);
    } catch {
      flashError(dispatch, 'Unable to save event type access.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={() => toggle(false)} className="modal-lg">
      <ModalHeader tag="div" className="custom-title w100">
        <div className="d-flex justify-content-between align-items-center w-100">
          <h3>Event Type Access</h3>
          <ButtonToolbar>
            <Button outline color="primary" className="mr-3" onClick={() => toggle(false)}>
              Close
            </Button>
            <Button color="primary" disabled={saving || loading} onClick={onSave}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </ButtonToolbar>
        </div>
      </ModalHeader>

      <ModalBody>
        {loading && (
          <div className="manager-program-settings__empty">Loading event types…</div>
        )}

        {!loading && !eventTypes.length && (
          <div className="manager-program-settings__empty">No event types available.</div>
        )}

        {!loading && eventTypes.length > 0 && (
          <>
            <p style={{ fontSize: '0.83rem', color: '#64748b', marginBottom: '1.1rem' }}>
              Enable or disable each event type for managers. Disabled types will not appear
              in the Event Type dropdown when creating a reward event. Settings inherit from
              the parent program and can be overridden per sub-program.
            </p>
            <div className="event-type-access__list">
              {eventTypes.map((et) => {
                const isEnabled = !!accessMap[et.event_type_id];
                return (
                  <div key={et.event_type_id} className="event-type-access__row">
                    <div className="event-type-access__copy">
                      <span className="event-type-access__name">{et.event_type_name}</span>
                      {et.inherited && (
                        <span className="event-type-access__inherited">Inherited</span>
                      )}
                    </div>
                    <div className="event-type-access__toggle-wrap">
                      <span className={`event-type-access__status${isEnabled ? ' is-enabled' : ' is-disabled'}`}>
                        {isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <ToggleButtonField
                        name={`et-access-${et.event_type_id}`}
                        value={isEnabled}
                        onChange={(val) => onToggle(et.event_type_id, val)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default EventTypeAccessModal;
